import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, MapPin, Wallet, Clock, Target, StickyNote, History } from "lucide-react";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { CRM } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const STAGES = [
  "Nuevo",
  "Contactado",
  "Calificando",
  "Reunión Agendada",
  "En Evaluación",
  "Formalización",
  "Participante",
  "Descartado",
];

const LABEL_STYLE = {
  calificado: "text-emerald-400 border-emerald-500/30 bg-emerald-900/10",
  a_revisar: "text-[#FFBF57] border-[#FFBF57]/40 bg-[#FFBF57]/[0.08]",
  no_calificado: "text-neutral-400 border-white/10 bg-white/[0.02]",
};

export default function LeadDetail() {
  const { leadId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/leads/${leadId}`);
      setLead(r.data);
    } catch (e) {
      toast.error("Lead no encontrado");
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    load();
  }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStage = async (stage) => {
    try {
      await api.patch(`/leads/${leadId}/stage`, { stage });
      toast.success(`Movido a "${stage}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo mover");
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/leads/${leadId}/notes`, { body: note.trim() });
      setNote("");
      toast.success("Nota añadida");
      load();
    } catch (e) {
      toast.error("No se pudo añadir la nota");
    }
  };

  if (!lead) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]">
        <TopBar variant="dashboard" />
        <div className="max-w-4xl mx-auto p-10 text-neutral-500">Cargando…</div>
      </div>
    );
  }

  const s = LABEL_STYLE[lead.score_label] || LABEL_STYLE.no_calificado;
  const canStage = user && ["admin", "director", "ventas"].includes(user.role);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-testid={CRM.leadDetail}>
      <TopBar variant="dashboard" />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <Link to="/dashboard" className="text-neutral-500 hover:text-[#FFBF57] text-xs uppercase tracking-widest flex items-center gap-2">
          <ArrowLeft size={14} strokeWidth={1.5} /> Volver al pipeline
        </Link>

        <div className="mt-8 grid lg:grid-cols-12 gap-8">
          {/* Left column */}
          <div className="lg:col-span-8">
            <div className="border border-white/10 bg-[#111112] p-8">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                  <p className="eyebrow">Ficha</p>
                  <h1 className="font-display text-4xl mt-3">{lead.name}</h1>
                  <p className="text-neutral-500 text-sm mt-1">Creado {new Date(lead.created_at).toLocaleString("es-MX")}</p>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Score</div>
                  <div className={`inline-flex items-baseline gap-2 px-3 py-2 border mt-2 ${s}`}>
                    <span className="font-display text-3xl leading-none">{lead.score}</span>
                    <span className="text-[10px] uppercase tracking-widest">{lead.score_label}</span>
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mt-10">
                <Info icon={<Mail size={14} strokeWidth={1.5} />} label="Correo" value={lead.email} />
                <Info icon={<Phone size={14} strokeWidth={1.5} />} label="Teléfono" value={lead.phone} />
                <Info icon={<MapPin size={14} strokeWidth={1.5} />} label="Ciudad" value={lead.city} />
                <Info icon={<Wallet size={14} strokeWidth={1.5} />} label="Capital estimado" value={lead.capital_estimated} />
                <Info icon={<Clock size={14} strokeWidth={1.5} />} label="Horizonte" value={lead.horizon} />
                <Info icon={<Target size={14} strokeWidth={1.5} />} label="Tolerancia al riesgo" value={lead.risk_tolerance} />
                <Info icon={<Clock size={14} strokeWidth={1.5} />} label="Timing" value={lead.timing} />
                <Info label="Origen" value={lead.source || "landing"} />
              </div>

              <div className="mt-10 pt-6 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Expectativa declarada</div>
                <p className="text-neutral-300 mt-3 leading-relaxed">{lead.expectation || "—"}</p>
              </div>
            </div>

            {/* Notes */}
            <div className="mt-8 border border-white/10 bg-[#111112] p-8">
              <div className="flex items-center gap-2 text-[#FFBF57]">
                <StickyNote size={16} strokeWidth={1.5} />
                <p className="eyebrow" style={{ color: "#FFBF57" }}>Notas del asesor</p>
              </div>
              <div className="mt-6 space-y-4">
                {(lead.notes || []).length === 0 && (
                  <p className="text-neutral-600 text-sm italic">Aún no hay notas.</p>
                )}
                {(lead.notes || []).slice().reverse().map((n) => (
                  <div key={n.note_id} className="border-l border-[#FFBF57]/40 pl-4 py-1">
                    <div className="text-[11px] text-neutral-500 uppercase tracking-widest">{n.author_name} · {new Date(n.created_at).toLocaleString("es-MX")}</div>
                    <p className="text-neutral-200 mt-2 leading-relaxed whitespace-pre-wrap">{n.body}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <textarea
                  data-testid={CRM.noteInput}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Escribe una nota de seguimiento…"
                  className="w-full bg-transparent border border-white/10 focus:border-[#FFBF57] outline-none px-4 py-3 text-sm min-h-[90px] transition-colors"
                />
                <div className="mt-3 flex justify-end">
                  <button className="btn-outline !py-2 !px-4 !text-xs" onClick={addNote} data-testid={CRM.addNoteBtn}>
                    Guardar nota
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right column: stages + timeline */}
          <div className="lg:col-span-4">
            <div className="border border-white/10 bg-[#111112] p-6">
              <p className="eyebrow">Etapa</p>
              <div className="mt-4 space-y-1.5">
                {STAGES.map((s2) => (
                  <button
                    key={s2}
                    disabled={!canStage}
                    onClick={() => changeStage(s2)}
                    data-testid={`stage-${s2}`}
                    className={`w-full text-left px-3 py-2 text-sm border transition-colors ${lead.stage === s2 ? "border-[#FFBF57] text-[#FFBF57] bg-[#FFBF57]/[0.05]" : "border-white/10 text-neutral-400 hover:text-white hover:border-white/25"} ${!canStage ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {s2}
                  </button>
                ))}
              </div>
              {!canStage && (
                <p className="text-[11px] text-neutral-600 mt-4 italic">Tu rol es de sólo lectura.</p>
              )}
            </div>

            <div className="mt-6 border border-white/10 bg-[#111112] p-6">
              <div className="flex items-center gap-2 text-[#FFBF57]">
                <History size={16} strokeWidth={1.5} />
                <p className="eyebrow" style={{ color: "#FFBF57" }}>Historial</p>
              </div>
              <div className="mt-6 space-y-4">
                {(lead.interactions || []).slice().reverse().map((it, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-2 h-2 mt-1.5 rounded-full bg-[#FFBF57] shrink-0" />
                    <div className="flex-1">
                      <div className="text-neutral-300 text-sm leading-relaxed">{it.body}</div>
                      <div className="text-[11px] text-neutral-600 mt-1">{new Date(it.created_at).toLocaleString("es-MX")}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Info({ icon, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-2">
        {icon && <span className="text-[#FFBF57]">{icon}</span>} {label}
      </div>
      <div className="text-neutral-200 text-sm mt-2">{value || "—"}</div>
    </div>
  );
}
